import { Check, ChevronRight, ChevronsRight, X } from "lucide-react";
import { useState } from "react";
import { handleCancel } from "../../UserPage/ProfileTab";
import Modal from "react-modal";

export default function PlanModal({
  token,
  darkMode,
  modalOpen,
  closeModal,
  userLevel,
  openPaymentModal,
}) {
  const [, setLoading] = useState(false);
  const [, setIsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const confirmCancel = () => {
    handleCancel(setLoading, setIsDisabled, token);
    closeModal();
  };

  return (
    <>
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Payments"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40">
        <div
          className={`rounded-lg shadow-lg max-w-2xl w-full p-6 relative max-h-full overflow-y-auto text-gray-900 dark:text-gray-100 ${
            darkMode ? "bg-slate-800" : "bg-slate-100"
          }`}>
          <button
            onClick={closeModal}
            className={`absolute top-4 right-4 p-1 hover:bg-red-500 hover:text-gray-200 rounded-full ${
              darkMode ? "text-gray-100" : "text-gray-900"
            }`}>
            <X strokeWidth={2.5} />
          </button>
          <div className="mb-6 text-center">
            <h2 className={`text-2xl font-bold`}>Upgrade Your Plan</h2>
            <p className={`${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              Choose the plan that suits your needs.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-y-4 sm:flex-row sm:space-y-0 sm:justify-between sm:gap-x-4">
            <div
              className={`flex flex-col w-full p-5 ring-1 dark:bg-slate-700 bg-slate-200 rounded-lg ${
                userLevel === "free"
                  ? "ring-green-700 dark:ring-green-400"
                  : "dark:ring-slate-600  ring-slate-300"
              }`}>
              <h3 className={`text-lg mb-1 font-bold`}>Free</h3>

              <p className={`text-2xl font-bold`}>$0/month</p>
              {userLevel === "free" ? (
                <div className="flex justify-center p-2 mt-2 font-semibold text-green-600 rounded-full ring-2 ring-green-600">
                  <span>Your Current Plan</span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                  className="flex justify-center w-full p-2 mt-2 font-semibold text-green-200 bg-green-700 rounded-full hover:bg-green-800">
                  <span>Revert to Free</span>
                </button>
              )}
              <ul
                className={`flex-grow pl-4 text-sm mt-4 text-left list-disc space-y-1`}>
                <li>Assistance with writing, problem solving, and more</li>
                <li>Limited access to GPT-4o mini</li>
                <li>
                  Limited access to advanced data analysis and web browsing
                </li>
              </ul>
            </div>
            {/* Premium Plan */}
            <div
              className={`flex relative flex-col w-full p-5 ring-1 dark:bg-slate-700 bg-slate-200 rounded-lg ${
                userLevel === "premium"
                  ? "ring-green-700 dark:ring-green-400"
                  : "ring-slate-300 dark:ring-slate-600"
              }`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg mb-1 font-bold`}>Premium</h3>
                {userLevel === "premium" && (
                  <span className="px-2 py-1 text-sm font-bold text-green-700 border-2 border-green-700 rounded-full dark:text-green-600 dark:border-green-600">
                    Current Plan
                  </span>
                )}
              </div>
              <p className={`text-2xl font-bold`}>$16.99/month</p>
              {userLevel === "premium" ? (
                <button className="flex justify-center p-2 mt-2 font-semibold text-green-200 bg-green-700 rounded-full hover:bg-green-800">
                  Change payment method
                </button>
              ) : (
                <button
                  onClick={openPaymentModal}
                  className="flex justify-center w-full p-2 mt-2 font-semibold text-green-200 bg-green-700 rounded-full hover:bg-green-800">
                  Get Premium
                </button>
              )}
              <ul
                className={`flex-grow pl-4 mt-4 text-left text-sm space-y-1 list-disc`}>
                <li>Early access to new features</li>
                <li>
                  Access to advanced data analysis, file uploads, vision, and
                  web browsing
                </li>
                <li>Comprehensive access to GPT-4</li>
              </ul>
            </div>
          </div>
        </div>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-lg p-5 text-sm rounded-lg shadow-2xl dark:bg-slate-800 bg-slate-100">
              <button
                className="absolute p-1 rounded-full top-4 right-4 hover:bg-red-500 hover:text-gray-200"
                onClick={() => setIsModalOpen(false)}>
                <X strokeWidth={2.5} />
              </button>
              <h2 className="mt-1 mb-4 text-xl font-semibold">
                Cancel Subscription
              </h2>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                Your subscription will be cancelled at the end of your billing
                period on <strong>2024</strong>, and you will not be charged
                anymore.
              </p>
              <p className="mb-2 text-gray-600 dark:text-gray-300">
                After this date, you will lose access to:
              </p>
              <div className="flex flex-col gap-2 pl-4 mb-4 text-sm font-semibold text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-1">
                  <ChevronsRight
                    size={20}
                    strokeWidth={2.5}
                  />
                  <span>Unlimited Tokens</span>
                </div>
                <div className="flex items-center gap-1">
                  <ChevronsRight
                    size={20}
                    strokeWidth={2.5}
                  />
                  <span>File Uploading</span>
                </div>
                <div className="flex items-center gap-1">
                  <ChevronsRight
                    size={20}
                    strokeWidth={2.5}
                  />
                  <span>Access to GPT-4</span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg dark:hover:bg-slate-600 hover:bg-slate-300">
                  Keep Subscription
                </button>
                <button
                  onClick={confirmCancel}
                  className="px-4 py-2 text-gray-100 bg-red-500 rounded-lg hover:bg-red-600">
                  Confirm Cancellation
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
